import moment from 'moment';
import {
  DATE_FORMAT_MAP,
  TIME_FORMAT_MAP,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT
} from '../Constants/StringConstants';

/**
 * Centralized date/time formatting driven by the user's display preference.
 *
 * The preferred format enums (`dateFormat` / `timeFormat`) are stored on the user
 * profile and persisted to `localStorage.user` at login (via the profile GET response).
 * Reading from localStorage keeps these helpers usable from both React components and
 * plain-JS modules (e.g. table column configs).
 */

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user')) || {};
  } catch (e) {
    return {};
  }
};

export const getUserDateFormat = () => {
  const user = getStoredUser();
  const dateFormat = user.dateTimeConfig?.dateFormat || user.dateFormat;
  return DATE_FORMAT_MAP[dateFormat] || DATE_FORMAT_MAP[DEFAULT_DATE_FORMAT];
};

export const getUserTimeFormat = () => {
  const user = getStoredUser();
  const timeFormat = user.dateTimeConfig?.timeFormat || user.timeFormat;
  return TIME_FORMAT_MAP[timeFormat] || TIME_FORMAT_MAP[DEFAULT_TIME_FORMAT];
};

export const formatDate = (value, fallback = '') => {
  if (!value) return fallback;
  const m = moment(value);
  return m.isValid() ? m.format(getUserDateFormat()) : fallback;
};

export const formatTime = (value, fallback = '') => {
  if (!value) return fallback;
  const m = moment(value);
  return m.isValid() ? m.format(getUserTimeFormat()) : fallback;
};

export const formatDateTime = (value, fallback = '') => {
  if (!value) return fallback;
  const m = moment(value);
  return m.isValid()
    ? m.format(`${getUserDateFormat()} ${getUserTimeFormat()}`)
    : fallback;
};

/**
 * The browser's current time zone as { displayName, utcOffset }, e.g.
 * { displayName: 'IST', utcOffset: '+05:30' } or { displayName: 'EDT',
 * utcOffset: '-04:00' }.
 *
 * `utcOffset` is computed from the local offset. `displayName` uses the Intl
 * short zone name when it's a real abbreviation (EDT, HST, HDT, PDT, …) and
 * falls back to the initials of the long zone name for zones Intl reports as
 * "GMT+…" (e.g. "India Standard Time" -> "IST").
 */
export const getUserTimeZoneConfig = () => {
  // Fallback when the time zone can't be determined for any reason.
  const DEFAULT = { displayName: 'UTC', utcOffset: '00:00' };
  try {
    const now = new Date();

    // utcOffset: +HH:MM / -HH:MM (minutes east of UTC)
    const offsetMinutes = -now.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    const utcOffset = `${sign}${hh}:${mm}`;

    // displayName: timezone abbreviation
    const partOf = options => {
      const part = new Intl.DateTimeFormat('en-US', options)
        .formatToParts(now)
        .find(p => p.type === 'timeZoneName');
      return part ? part.value : '';
    };
    const shortName = partOf({ timeZoneName: 'short' });
    let displayName;
    if (/^[A-Za-z]{2,6}$/.test(shortName)) {
      displayName = shortName.toUpperCase();
    } else {
      const longName = partOf({ timeZoneName: 'long' });
      displayName = longName
        .split(/[\s-]+/)
        .filter(Boolean)
        .map(word => word[0])
        .join('')
        .toUpperCase();
    }

    if (!displayName || !utcOffset) return DEFAULT;
    return { displayName, utcOffset };
  } catch (e) {
    return DEFAULT;
  }
};
